import { useEffect, useState } from 'react'
import { useBulk } from '../store/bulk'
import { generateAliases } from '../utils/aliasEmail'
import { generateCandidates, type AiCandidate, type AiProvider } from '../utils/aiNames'
import {
  clearVault,
  loadVault,
  saveVault,
  vaultExists,
  type VaultEntry,
} from '../utils/passwordVault'

type Row = {
  index: number
  email: string
  tag: string
  handle: string
  displayName: string
  bio: string
  password: string
  ready: boolean
}

function strongPassword(): string {
  const buf = new Uint8Array(18)
  crypto.getRandomValues(buf)
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('')
}

const PROVIDERS: { key: AiProvider; label: string; placeholder: string }[] = [
  { key: 'openai', label: 'OpenAI', placeholder: 'sk-…' },
  { key: 'anthropic', label: 'Anthropic', placeholder: 'sk-ant-…' },
  { key: 'gemini', label: 'Gemini', placeholder: 'AIza…' },
  { key: 'xai', label: 'xAI Grok', placeholder: 'xai-…' },
]

export function BulkSignup() {
  const s = useBulk()
  const [rows, setRows] = useState<Row[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Vault state
  const [vaultPass, setVaultPass] = useState('')
  const [vault, setVault] = useState<VaultEntry[] | null>(null)
  const [vaultErr, setVaultErr] = useState<string | null>(null)
  const [vaultPresent, setVaultPresent] = useState(false)

  useEffect(() => {
    vaultExists().then(setVaultPresent)
  }, [])

  function regenerateAliases() {
    setError(null)
    try {
      const aliases = generateAliases({
        baseEmail: s.baseEmail,
        count: s.count,
        startAt: s.startAt,
      })
      setRows((prev) => {
        const map = new Map(prev.map((r) => [r.index, r]))
        return aliases.map((a) => {
          const existing = map.get(a.index)
          return {
            index: a.index,
            email: a.email,
            tag: a.tag,
            handle: existing?.handle ?? '',
            displayName: existing?.displayName ?? '',
            bio: existing?.bio ?? '',
            password: existing?.password ?? strongPassword(),
            ready: existing?.ready ?? false,
          }
        })
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function generateNames() {
    setBusy(true)
    setError(null)
    try {
      const candidates: AiCandidate[] = await generateCandidates({
        provider: s.provider,
        apiKey: s.apiKey,
        prompt: s.prompt,
        count: rows.length || s.count,
        model: s.model || undefined,
      })
      setRows((prev) =>
        prev.map((r, i) => {
          const c = candidates[i]
          if (!c) return r
          // Append the alias tag to the AI handle to keep them unique on Bluesky
          return {
            ...r,
            handle: `${c.handle || `user${r.index}`}-${r.tag}`,
            displayName: c.displayName,
            bio: c.bio,
          }
        }),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  function copy(text: string) {
    navigator.clipboard?.writeText(text).catch(() => undefined)
  }

  async function unlockVault() {
    setVaultErr(null)
    try {
      const data = await loadVault(vaultPass)
      setVault(data)
    } catch (err) {
      setVaultErr(err instanceof Error ? err.message : String(err))
    }
  }

  async function newVault() {
    if (!vaultPass) {
      setVaultErr('passphrase required to create a new vault')
      return
    }
    await saveVault(vaultPass, [])
    setVault([])
    setVaultPresent(true)
  }

  async function persistVault(next: VaultEntry[]) {
    if (!vaultPass) return
    await saveVault(vaultPass, next)
    setVault(next)
  }

  function importRow(r: Row) {
    if (!vault) return
    const entry: VaultEntry = {
      handle: r.handle,
      email: r.email,
      appPassword: '', // empty until the user pastes the bsky-generated app password
      notes: r.bio,
      createdAt: Date.now(),
    }
    persistVault([...vault.filter((e) => e.handle !== r.handle), entry])
  }

  return (
    <main className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      <div className="glass p-5">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">Bulk signup helper</h1>
            <p className="mt-1 max-w-3xl text-sm text-ink-200">
              Build the signup batch locally — alias emails, AI-generated personas, generated
              passwords. Each row gives you copy-and-open helpers so you can complete each
              signup at <code>bsky.app/signup</code>. The captcha-bypass + auto-create steps
              are deliberately not wired here; see <code>DEVHANDOFF.md</code>.
            </p>
          </div>
        </div>

        <section className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            base email
            <input
              className="glass-input"
              placeholder="you@gmail.com"
              value={s.baseEmail}
              onChange={(e) => s.setBaseEmail(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            count
            <input
              type="number"
              min={1}
              max={500}
              className="glass-input"
              value={s.count}
              onChange={(e) => s.setCount(Number(e.target.value))}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
            start at
            <input
              type="number"
              min={0}
              className="glass-input"
              value={s.startAt}
              onChange={(e) => s.setStartAt(Number(e.target.value))}
            />
          </label>
          <div className="flex flex-col justify-end">
            <button
              type="button"
              className="glass-button glass-button--accent"
              onClick={regenerateAliases}
              disabled={!s.baseEmail}
            >
              build alias list
            </button>
          </div>
        </section>

        <section className="mt-5 border-t border-white/5 pt-4">
          <div className="mb-2 text-xs uppercase tracking-widest text-ink-300">
            AI persona generator
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
              provider
              <select
                className="glass-input"
                value={s.provider}
                onChange={(e) => s.setProvider(e.target.value as AiProvider)}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
              api key
              <input
                type="password"
                className="glass-input"
                placeholder={PROVIDERS.find((p) => p.key === s.provider)?.placeholder}
                value={s.apiKey}
                onChange={(e) => s.setApiKey(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300 sm:col-span-2">
              persona prompt
              <textarea
                rows={2}
                className="glass-input resize-y"
                value={s.prompt}
                onChange={(e) => s.setPrompt(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink-300">
              model override (optional)
              <input
                className="glass-input"
                placeholder="gpt-4o-mini / claude-haiku-4-5-20251001 / …"
                value={s.model}
                onChange={(e) => s.setModel(e.target.value)}
              />
            </label>
            <div className="flex flex-col justify-end">
              <button
                type="button"
                className="glass-button glass-button--accent"
                onClick={generateNames}
                disabled={busy || rows.length === 0 || !s.apiKey}
              >
                {busy ? 'generating…' : `generate ${rows.length} personas`}
              </button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-ink-400">
            API keys live in <code>localStorage</code> under{' '}
            <code>bskydisc.bulk</code> and never leave the browser. The provider's CORS
            posture decides whether the call works direct from the browser; Anthropic
            requires the dangerous-direct-browser-access header (already set).
          </p>
        </section>

        {error ? (
          <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            {error}
          </div>
        ) : null}
      </div>

      {rows.length > 0 ? (
        <div className="glass mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-ink-400">
              <tr>
                <th className="p-2 text-left">#</th>
                <th className="p-2 text-left">email</th>
                <th className="p-2 text-left">handle</th>
                <th className="p-2 text-left">display name</th>
                <th className="p-2 text-left">bio</th>
                <th className="p-2 text-left">password</th>
                <th className="p-2 text-left">actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.index} className="border-t border-white/5 align-top">
                  <td className="p-2 font-mono">{r.index}</td>
                  <td className="p-2 font-mono">{r.email}</td>
                  <td className="p-2">
                    <input
                      className="glass-input w-full font-mono text-xs"
                      value={r.handle}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, handle: e.target.value } : x)),
                        )
                      }
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="glass-input w-full text-xs"
                      value={r.displayName}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x, i) =>
                            i === idx ? { ...x, displayName: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="p-2">
                    <textarea
                      rows={2}
                      className="glass-input w-full resize-y text-xs"
                      value={r.bio}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, bio: e.target.value } : x)),
                        )
                      }
                    />
                  </td>
                  <td className="p-2 font-mono">
                    <input
                      className="glass-input w-full text-[10px]"
                      value={r.password}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x, i) =>
                            i === idx ? { ...x, password: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="p-2">
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        className="glass-button text-[10px]"
                        onClick={() =>
                          copy(
                            JSON.stringify(
                              {
                                email: r.email,
                                handle: r.handle,
                                displayName: r.displayName,
                                bio: r.bio,
                                password: r.password,
                              },
                              null,
                              2,
                            ),
                          )
                        }
                      >
                        copy json
                      </button>
                      <a
                        className="glass-button text-[10px]"
                        href="https://bsky.app/signup"
                        target="_blank"
                        rel="noreferrer"
                        title="open the signup page; paste the JSON above into the form"
                      >
                        open signup ↗
                      </a>
                      {vault ? (
                        <button
                          type="button"
                          className="glass-button text-[10px]"
                          onClick={() => importRow(r)}
                        >
                          add to vault
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <section className="glass mt-3 p-4">
        <h2 className="text-sm font-bold">App-password vault</h2>
        <p className="mt-1 text-xs text-ink-300">
          Encrypted (AES-GCM, PBKDF2-derived key, 250k iterations) local store for the app
          passwords each new account generates inside Bluesky's settings. Passphrase is never
          persisted; you supply it every session.
        </p>
        {vault ? (
          <>
            <div className="mt-2 text-xs text-ink-200">
              {vault.length} entries • last saved{' '}
              {vault.length > 0 ? new Date(Math.max(...vault.map((v) => v.createdAt))).toLocaleString() : '—'}
            </div>
            <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-white/10 bg-black/20 p-2">
              {vault.map((v, i) => (
                <div key={`${v.handle}-${i}`} className="flex flex-wrap items-center gap-2 border-b border-white/5 py-1 text-[11px]">
                  <span className="font-semibold">@{v.handle}</span>
                  <span className="text-ink-300">{v.email}</span>
                  <input
                    className="glass-input min-w-[14rem] flex-1 font-mono text-[10px]"
                    placeholder="paste app password"
                    value={v.appPassword}
                    onChange={(e) => {
                      const next = [...vault]
                      next[i] = { ...v, appPassword: e.target.value }
                      persistVault(next)
                    }}
                  />
                  <button
                    type="button"
                    className="glass-button text-[10px]"
                    onClick={() => persistVault(vault.filter((_, j) => j !== i))}
                  >
                    remove
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className="glass-button text-xs"
                onClick={async () => {
                  if (confirm('Wipe the encrypted vault from this browser?')) {
                    await clearVault()
                    setVault(null)
                    setVaultPresent(false)
                    setVaultPass('')
                  }
                }}
              >
                wipe vault
              </button>
            </div>
          </>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              type="password"
              className="glass-input flex-1"
              placeholder="vault passphrase"
              value={vaultPass}
              onChange={(e) => setVaultPass(e.target.value)}
            />
            {vaultPresent ? (
              <button type="button" className="glass-button glass-button--accent text-xs" onClick={unlockVault}>
                unlock
              </button>
            ) : (
              <button type="button" className="glass-button glass-button--accent text-xs" onClick={newVault}>
                create new vault
              </button>
            )}
            {vaultErr ? <div className="text-xs text-rose-200">{vaultErr}</div> : null}
          </div>
        )}
      </section>
    </main>
  )
}
